import { KatalComponent, Keys } from '../../shared/base';
import { nextUniqueId } from '../../shared/base/unique-id';
import { createElement } from '../../shared/utils';

/**
 * @component {kat-modal} KatalModal Modal dialogs interrupt users and demand an action. They are appropriate when user’s attention needs to be directed toward important information
 * @guideline Do Modals can work effectively when the information being requested or presented is relevant or can streamline the completion of the current task.
 * @guideline Dont Do not use modal dialogs for nonessential information that is not related to the current user flow.
 * @contentProvided This is to let users know the components expects in inner html to be completed
 * @example SampleModal {"visible":false, "content":"<kat-modal-content><kat-modal-header>Modal Title</kat-modal-header><kat-modal-body><p>Lorem ipsum dolor sit amet, sit nihil utamur ne, alia persequeris sit id, usu ut erant quando. Primis deleniti scribentur sit ex.</p><p>The buttons in this example will not perform any actions.  It is up the the application's logic to decide what happens when buttons are added to a Modal</p></kat-modal-body><kat-modal-footer><kat-button variant=\"secondary\" label=\"Cancel\"></kat-button><kat-button label=\"Submit\"></kat-button></kat-modal-footer></kat-modal-content>"}
 * @example LongModal {"visible":false, "content":"<kat-modal-content><kat-modal-header>Modal Title</kat-modal-header><kat-modal-body><p>Lorem ipsum dolor sit amet, sit nihil utamur ne, alia persequeris sit id, usu ut erant quando. Primis deleniti scribentur sit ex.</p><p>The buttons in this example will not perform any actions.  It is up the the application's logic to decide what happens when buttons are added to a Modal</p><kat-box variant=\"white-junglemist\"><p>Long overflow content... keep scrolling</p><p>Long overflow content... keep scrolling</p><p>Long overflow content... keep scrolling</p><p>Long overflow content... keep scrolling</p><p>Long overflow content... keep scrolling</p><p>Long overflow content... keep scrolling</p><p>Long overflow content... keep scrolling</p><p>Long overflow content... keep scrolling</p><p>Long overflow content... keep scrolling</p><p>Long overflow content... keep scrolling</p><p>Long overflow content... keep scrolling</p><p>Long overflow content... keep scrolling</p><p>Long overflow content... keep scrolling</p><p>Long overflow content... keep scrolling</p><p>Long overflow content... keep scrolling</p><p>Long overflow content... keep scrolling</p><p>Long overflow content... keep scrolling</p><p>Long overflow content... keep scrolling</p><p>Long overflow content... keep scrolling</p><p>You've reached the bottom!</p></kat-box></kat-modal-body><kat-modal-footer><kat-button variant=\"secondary\" label=\"Cancel\"></kat-button><kat-button label=\"Submit\"></kat-button></kat-modal-footer></kat-modal-content>"}
 * @event opened Fires when the modal dialog is opened
 * @event dismissed Fired when the modal dialog is closed
 * @status Production
 * @theme flo
 */
class KatalModal extends KatalComponent {
  static get observedAttributes() {
    return ['visible', 'scroll-all'];
  }

  constructor() {
    super(KatalModal.observedAttributes);

    this._clickStartedOnModal = false;

    this._onDocumentKeyDown = this._onDocumentKeyDown.bind(this);

    this.addEventListener('mousedown', this.mouseDown);
    this.addEventListener('mouseup', this.mouseUp);
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('keydown', this._onDocumentKeyDown);

    this.setAttribute('role', 'dialog');
    this.setAttribute('aria-modal', 'true');

    const header = this.querySelector('kat-modal-header');
    if (header) {
      if (!header.id) {
        header.id = nextUniqueId();
      }
      this.setAttribute('aria-labelledby', header.id);
    }
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this._onDocumentKeyDown);
  }

  setupCloseIconIfMissing() {
    const header = this.querySelector('kat-modal-header');
    if (header) {
      const closeIcon = header.querySelector('.kat-modal-close-icon');
      if (!closeIcon) {
        const closeButton = createElement(
          'button',
          {
            type: 'button',
            class: 'kat-modal-close-icon kat-no-style',
          },
          [
            createElement('kat-icon', {
              name: 'x',
              size: 'small',
            }),
          ]
        );
        header.appendChild(closeButton);
        closeButton.addEventListener('click', () => {
          this.close();
        });
      }
    }
  }

  mouseDown(evt) {
    this._clickStartedOnModal = evt.target === this;
  }

  mouseUp(evt) {
    if (evt.target === this && this._clickStartedOnModal) {
      this.close();
    }

    this._clickStartedOnModal = false;
  }

  _onDocumentKeyDown(evt) {
    if (this.visible && evt.keyCode === Keys.Escape) {
      this.close();
    }
  }

  /**
   * Call to close the modal. Fires the dismissed event.
   *
   * @katalmethod
   */
  close() {
    this.removeAttribute('visible');
    this.dispatchCustomEvent('dismissed', { visible: this.visible });
  }

  /**
   * Call to open the modal. Fires the opened event.
   *
   * @katalmethod
   */
  open() {
    this.setAttribute('visible', 'true');
    this.dispatchCustomEvent('opened', { visible: this.visible });
  }

  /**
   * @classprop {boolean} visible determines if the dialog is open or closed.
   */
  get visible() {
    return this.getBooleanAttribute('visible');
  }

  set visible(value) {
    this.setBooleanAttribute('visible', value);
  }

  /**
   * @classprop {boolean} scroll-all Scroll the entirety of the modal along the page (no fixed header + footer). Defaults to false
   */
  get scrollAll() {
    return this.getBooleanAttribute('scroll-all');
  }

  set scrollAll(value) {
    this.setBooleanAttribute('scroll-all', value);
  }

  attributeChangedCallback(attr) {
    if (attr === 'visible') {
      this.setupCloseIconIfMissing();
    }
  }
}

KatalComponent.register('kat-modal', KatalModal);
